import { dayjs } from '@/shared/lib/dayjs';

import type { Card } from './payments.schema';

export type { Card };

/** `MM/YY`, zero-padded. Stripe sends the month as a number, so `3` has to
 *  become `03` — `3/28` reads as a malformed date. */
export const formatExpiry = (card: Card): string => {
  if (card.exp_month === 0 || card.exp_year === 0) return '—';

  const month = String(card.exp_month).padStart(2, '0');
  const year = String(card.exp_year).slice(-2);

  return `${month}/${year}`;
};

export type ExpiryStatus = 'valid' | 'expiring' | 'expired';

/**
 * A card expires at the END of its stated month, which is why this compares
 * against the month's last day rather than its first. Treating `03/26` as dead
 * on 1 March would flag a card that still has a month of life in it.
 *
 * "Expiring" is the 60-day window before that — enough warning for a director
 * to replace a card before an annual review payment fails.
 */
export const expiryStatusOf = (card: Card): ExpiryStatus => {
  if (card.exp_month === 0 || card.exp_year === 0) return 'valid';

  const expiresAt = dayjs()
    .year(card.exp_year)
    .month(card.exp_month - 1)
    .endOf('month');

  if (!expiresAt.isValid()) return 'valid';

  const now = dayjs();
  if (expiresAt.isBefore(now)) return 'expired';

  return expiresAt.diff(now, 'day') <= 60 ? 'expiring' : 'valid';
};

/**
 * The default card first, the rest in the order the API returned them.
 *
 * Stripe lists cards by creation date, so without this the card in use drifts
 * to wherever it happens to fall — and on a phone the list is a single column,
 * which means the one card that matters can sit below the fold. `toSorted`
 * leaves the query's cached array untouched; sorting it in place would mutate
 * RTK Query's state.
 */
export const sortCardsByDefault = (cards: Card[], defaultCardId: string): Card[] =>
  cards.toSorted((a, b) => {
    if (a.id === b.id) return 0;
    if (a.id === defaultCardId) return -1;
    if (b.id === defaultCardId) return 1;
    return 0;
  });
